/**
 * The three calibrated themes, as CONTENT.
 *
 * `design/DESIGN-SYSTEM.md` §4.7, verbatim: "`editor-design` and `library`
 * render warm and olive palettes (`#F7F5F1` · `#3A2F26` · `#B4531F` · `#12211C`
 * · …). These are **respondent-form theme presets on display** — data from the
 * `presets` table (PRD §3.3), not Leadgible UI colour. **Deliberately excluded
 * from `tokens.css`.** A theme picker renders whatever a preset holds; it does
 * not draw from the token file."
 *
 * So the hex values below are the one place in `packages/app` where a colour
 * literal is correct rather than a defect. They are not Leadgible's colours —
 * they are a respondent-form palette an operator chose, the same kind of value
 * as a funnel's name. `scripts/gates.sh` scans changed source for raw hex, so
 * this file is where that scan has to be argued with rather than worked around,
 * and the argument is §4.7.
 *
 * THEY LIVE HERE AND NOT IN THE DATABASE YET. PRD §3.3's `presets` table is
 * Story 7.5's, and the three the export draws are fixed and calibrated. When
 * 7.5 lands, this array becomes its seed and the picker reads rows instead —
 * `themeRefSchema`'s `preset_id` is already what the funnel stores, so nothing
 * about the funnel shape changes when it does.
 *
 * Story 6.5.
 */

/** What one theme card draws: a miniature of the form it produces. */
export type ThemePreset = {
  id: string
  name: string
  what: string
  /** The card's miniature — page ground, its edge, and the three dots. */
  ground: string
  edge: string
  heading: string
  muted: string
  accent: string
  /** The miniature's button radius, which is the theme's own control radius. */
  buttonRadius: string
  /** The bar radius — square-ish on Precise, rounder on Warm. */
  barRadius: string
  dots: [string, string, string]
}

export const THEMES: readonly ThemePreset[] = [
  {
    id: 'leadgible_clear',
    name: 'Leadgible Clear',
    what: 'The default. Cool neutrals, generous spacing, nothing to argue with.',
    ground: '#FFFFFF',
    edge: '#E2E7EC',
    heading: '#1A1D21',
    muted: '#C3CBD4',
    accent: '#1A6FE0',
    buttonRadius: '10px',
    barRadius: '3px',
    dots: ['#1A6FE0', '#E7EFFC', '#1A1D21'],
  },
  {
    id: 'warm',
    name: 'Warm',
    what: 'Softer ground and rounder controls. Suits trades and home services.',
    ground: '#FBF7F2',
    edge: '#EADFD1',
    heading: '#3A2F26',
    muted: '#C4B4A2',
    accent: '#B4531F',
    buttonRadius: '14px',
    barRadius: '3px',
    dots: ['#B4531F', '#F0E3D2', '#3A2F26'],
  },
  {
    id: 'precise',
    name: 'Precise',
    what: 'Tighter type and squarer edges. Suits consultancies and B2B services.',
    ground: '#F2F4F3',
    edge: '#D8DEDB',
    heading: '#12211C',
    muted: '#9AA6A1',
    accent: '#12211C',
    buttonRadius: '4px',
    barRadius: '2px',
    dots: ['#12211C', '#D8DEDB', '#1FA97A'],
  },
]

/** "Match my website" — inherit mode, and its own miniature in the export. */
export const INHERIT_CARD = {
  name: 'Match my website',
  what: "Reads your page's fonts and colours so the funnel doesn't look pasted on.",
  ground: '#F7F5F1',
  edge: '#E4DED2',
  heading: '#2B3A32',
  muted: '#B6BCB4',
  accent: '#1E5F4B',
  buttonRadius: '6px',
  barRadius: '3px',
  dots: ['#1E5F4B', '#C97A2B', '#2B3A32'] as [string, string, string],
}

/** PRD §7.5: "Design | Theme selection; **token editor deferred to M6**". */
export const M6_DEFERRAL = 'Full colour and type editing arrives later. These three are calibrated.'

/** The four layout modes, with the export's own copy and its one recommendation. */
export const LAYOUTS = [
  {
    id: 'wizard',
    name: 'Wizard',
    what: 'One question per screen — the highest completion rate on mobile.',
    recommended: true,
  },
  {
    id: 'grouped',
    name: 'Grouped',
    what: 'Related questions on one screen, a few screens in total.',
    recommended: false,
  },
  {
    id: 'single_page',
    name: 'Single page',
    what: 'Everything at once. Best when people already know they want to enquire.',
    recommended: false,
  },
  {
    id: 'conversational',
    name: 'Conversational',
    what: 'Questions arrive as a chat. Slower to finish, warmer to read.',
    recommended: false,
  },
] as const

/** The three progress styles. */
export const PROGRESS = [
  { id: 'bar', name: 'Bar' },
  { id: 'steps', name: 'Steps' },
  { id: 'none', name: 'None' },
] as const

/**
 * Progress applies to Wizard and Grouped only — the export says so on the card.
 *
 * Single page shows everything at once and Conversational has no discrete
 * screens, so neither has a position to report.
 */
export const PROGRESS_APPLIES_TO: readonly string[] = ['wizard', 'grouped']
export const progressApplies = (layout: string): boolean => PROGRESS_APPLIES_TO.includes(layout)
